import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2 } from 'lucide-react';

export const HeroSection = ({ onCTAClick }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_hsl(210_100%_56%_/_0.15),_transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_hsl(158_64%_52%_/_0.1),_transparent_50%)]" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(220_15%_20%_/_0.1)_1px,transparent_1px),linear-gradient(to_bottom,hsl(220_15%_20%_/_0.1)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_at_center,black_50%,transparent_100%)]" />
      
      <div className="container mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs sm:text-sm px-3 py-1">
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              Patent Pending
            </Badge>
            <Badge className="bg-secondary/10 text-secondary border-secondary/20 hover:bg-secondary/20 text-xs sm:text-sm px-3 py-1">
              Prototype in Development
            </Badge>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight font-['Space_Grotesk'] px-2">
            Fair football starts with
            <span className="block mt-2 bg-gradient-to-r from-primary via-primary-glow to-secondary bg-clip-text text-transparent">
              fair decisions
            </span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
            Anti-cheat technology for grassroots football. Built to support referees and eliminate incorrect decisions.
          </p>
          
          {/* Audience Line */}
          <p className="text-xs sm:text-sm md:text-base text-foreground/70 max-w-xl mx-auto italic px-4">
            For clubs, referees and leagues who want fairer football.
          </p>
          
          {/* CTA Button */}
          <div className="pt-4 px-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Button 
                onClick={onCTAClick}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm sm:text-base lg:text-lg px-6 sm:px-8 lg:px-12 h-11 sm:h-12 lg:h-14 rounded-full glow-primary group w-full sm:w-auto"
              >
                Join Early Access
                <svg className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
              
              <Button 
                onClick={() => window.location.href = '/pilot-club'}
                variant="outline"
                size="lg"
                className="border-accent/30 text-accent hover:bg-accent/10 hover:border-accent font-semibold text-sm sm:text-base lg:text-lg px-6 sm:px-8 lg:px-12 h-11 sm:h-12 lg:h-14 rounded-full w-full sm:w-auto"
              >
                Apply as a Pilot Club
              </Button>
            </div>
            
            {/* Credibility Line */}
            <p className="text-xs sm:text-sm text-muted-foreground mt-4 max-w-xl mx-auto px-2">
              Currently in prototype development with early interest from leading football organisations.
            </p>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 pt-8 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span>Built for Grassroots Football</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span>Protecting Referees</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span>Accuracy Without Compromise</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};
